import classNames from "classnames";
import {
    CarouselDots,
    NextSlideButton,
    PreviousSlideButton,
} from "components/GenericCarousel";
import { useRouterParams } from "components/useRouterParams";
import {
    imagePlaceholder,
    slugify,
    useIsClient,
    useVisibleSlidesByBreakpoint,
    useWindowWidth,
} from "helpers/utils";
import { StreamfieldBlock } from "interfaces";
import { useRouter } from "next/router";
import { CarouselProvider, Slide, Slider } from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import { Tab, TabList, TabPanel, Tabs } from "react-aria-components";
import { CloudinaryImage } from "react-tiny-cloudinary";

import { Filter, getUniqueFilters } from "../GridFilters";

import sassVariables from "../../styles/variables.module.scss";
import styles from "./featured-grid-filters.module.scss";

export type FeaturedGridFiltersCmsData = {
    filters: Array<Filter>;
    nonzeroFilterIDs?: any;
};

export type FeaturedGridFiltersBlock = StreamfieldBlock<
    "featured_grid_filters",
    FeaturedGridFiltersCmsData
>;

export function FeaturedGridFilters(props: FeaturedGridFiltersCmsData) {
    const isClient = useIsClient();
    const router = useRouter();
    const routerParams = useRouterParams({
        method: "push",
        shallow: true,
        scroll: false,
    });
    const visibleSlides = useVisibleSlidesByBreakpoint(7, 3, 5);
    const windowWidth = useWindowWidth();
    const showCarouselDots = windowWidth
        ? windowWidth < parseInt(sassVariables.breakpointMobile)
        : false;

    const featuredFilters = props.filters.filter((attribute) => {
        return attribute.is_featured === true;
    });

    if (!isClient || featuredFilters.length === 0) {
        return null;
    }

    const onFilterChange = (name: string, value: string) => {
        delete router.query.offset;
        routerParams.toggleParam(name, value);
    };

    const willReturnZero = (attributeID: number, optionID: number) => {
        if (!props.nonzeroFilterIDs) {
            return false;
        }

        const hasOptionID =
            props.nonzeroFilterIDs[attributeID].includes(optionID);

        if (hasOptionID) {
            return false;
        }

        return true;
    };

    const isFilterIDInParams = (attributeID: number) => {
        const queryKeys = [...Object.keys(router.query)];

        return queryKeys.includes(attributeID.toString());
    };

    const renderFilter = (filter: Filter) => {
        // Don't show Filter Attributes that contain no option_types
        if (filter.options.length === 0) {
            return null;
        }

        const uniqueFilters = getUniqueFilters(filter.options);

        return (
            <TabPanel
                key={filter.attribute.id}
                id={filter.attribute.id.toString()}
                className={classNames({
                    [styles.noCarousel]: uniqueFilters.length < 8,
                })}
            >
                <CarouselProvider
                    currentSlide={0}
                    naturalSlideHeight={150}
                    naturalSlideWidth={75}
                    infinite={false}
                    isIntrinsicHeight={true}
                    totalSlides={uniqueFilters.length}
                    visibleSlides={visibleSlides}
                    className={styles.tileCarousel}
                    step={visibleSlides}
                >
                    {uniqueFilters.length > 7 && (
                        <>
                            <PreviousSlideButton
                                className={`${styles.prev} al-featured-grid-filters__prev`}
                            />
                            <NextSlideButton
                                className={`${styles.next} al-featured-grid-filters__next`}
                            />
                        </>
                    )}
                    <Slider trayTag="ul">
                        {uniqueFilters.map((tile, index) => (
                            <Slide
                                key={tile.id}
                                index={index}
                                className={classNames({
                                    [styles.tile]: true,
                                    [styles.tileSelected]:
                                        routerParams.hasParam(
                                            filter.attribute.id.toString(),
                                            tile.id.toString(),
                                        ),
                                    [styles.tileDisabled]:
                                        willReturnZero(
                                            filter.attribute.id,
                                            tile.id,
                                        ) &&
                                        !isFilterIDInParams(
                                            filter.attribute.id,
                                        ),
                                    ["al-featured-grid-filters__tile"]: true,
                                    [`al-featured-grid-filters__tile--${slugify(
                                        tile.option,
                                    )}`]: true,
                                })}
                                tag="li"
                            >
                                <button
                                    onClick={() =>
                                        onFilterChange(
                                            filter.attribute.id.toString(),
                                            tile.id.toString(),
                                        )
                                    }
                                >
                                    <CloudinaryImage
                                        type={
                                            tile.primary_image
                                                ? "fetch"
                                                : "upload"
                                        }
                                        cldId={
                                            tile.primary_image?.image ??
                                            imagePlaceholder
                                        }
                                    >
                                        <img className={styles.icon} alt="" />
                                    </CloudinaryImage>
                                    {filter.show_label && (
                                        <p className={styles.filterName}>
                                            {tile.option}
                                        </p>
                                    )}
                                </button>
                            </Slide>
                        ))}
                    </Slider>
                    {showCarouselDots && (
                        <CarouselDots dotGroupClass="al-featured-grid-filters__dots" />
                    )}
                </CarouselProvider>
            </TabPanel>
        );
    };

    return (
        <section className={styles.featuredGridFilters}>
            <Tabs>
                <TabList className={styles.tabs}>
                    <span className={styles.label}>Shop By</span>
                    <div className={styles.tabContainer}>
                        {featuredFilters.map((filter) => (
                            <Tab
                                key={filter.attribute.id}
                                id={filter.attribute.id.toString()}
                                className={classNames({
                                    [styles.tab]: true,
                                    "al-featured-grid-filters__tab": true,
                                    [`al-featured-grid-filters__tab--${slugify(
                                        filter.display_name,
                                    )}`]: true,
                                })}
                            >
                                {filter.display_name}
                            </Tab>
                        ))}
                    </div>
                </TabList>
                <div className={styles.tabPanels}>
                    {featuredFilters.map((filter) => {
                        return renderFilter(filter);
                    })}
                </div>
            </Tabs>
        </section>
    );
}
